import { ColumnType } from "antd/es/table";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import { PhasesListViewProps } from "../ProjectScheduleManagement/phases/PhasesListView";
import { TooltipComponent } from "../../components/Component";
import { Drawer, Table, Progress, Tooltip } from "antd";
import dayjs from "dayjs";
import { tasksApis } from "../../api/NewTasks";

interface TaskItem {
  taskId: string;
  taskName: string;
  taskOwnerName: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  priorityName: string;
  completionPercentage: number;
  taskStatus: string;
  uid: string;
}

const UserWisePlannedActualsReport = () => {
  const [OwnerFilterData, setOwnerFilterData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [tasksData, setTasksData] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: projectId, projectName } = location.state || {};

  const columns: ColumnType<any>[] = [
    {
      title: "User Name",
      dataIndex: "assigedTo",
      key: "assigedTo",
      width: 100,
      render: (text) => (text && text.trim() !== "" ? text : "N/A"),
    },
    {
      title: "Role Name",
      dataIndex: "roleName",
      key: "roleName",
      width: 100,
      render: (text) => (text && text.trim() !== "" ? text : "N/A"),
    },
    {
      title: "Reporting To",
      dataIndex: "reportingToName",
      key: "reportingToName",
      width: 150,
      render: (text) => (text && text.trim() !== "" ? text : "N/A"),
    },
    {
      title: "Planned Tasks",
      dataIndex: "totalPlannedTasks",
      key: "totalPlannedTasks",
      width: 100,
      render(text: any, record: PhasesListViewProps) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`phase-${record?.phaseId}`}
              onClick={() => handlePlannedTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.phaseId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Completed Tasks",
      dataIndex: "totalCompletedTasks",
      key: "totalCompletedTasks",
      width: 100,
      render(text: any, record: PhasesListViewProps) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`phase-${record?.phaseId}`}
              onClick={() => handleCompletedTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.phaseId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Planned Hours",
      dataIndex: "totalPlannedHours",
      key: "totalPlannedHours",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Actual Hours",
      dataIndex: "totalActualHours",
      key: "totalActualHours",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Variance",
      dataIndex: "totalVariance",
      key: "totalVariance",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Completed Percentage",
      dataIndex: "completionPercentage",
      key: "completionPercentage",
      width: 200,
      render: (text, record) => {
        if (
          text === null ||
          text === undefined ||
          text === "" ||
          text === "N/A"
        )
          return "N/A";

        const percentage = parseFloat(text.toString().replace("%", ""));

        return (
          <Tooltip title={`Completion: ${text}`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const handlePlannedTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompletedTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskStatus: "Closed",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerFilterData = async () => {
    try {
      const response = await tasksApis.getOwnersFilterData(
        projectId?.toString()
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setOwnerFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    fetchOwnerFilterData();
    dispatch(
      updateMenuData([
        {
          text: "User-Wise Planned vs Actual Report",
          link: "/pms/userwise-planned-actuals-report",
        },
      ])
    );
  }, []);

  const taskColumns: ColumnType<TaskItem>[] = useMemo(
    () => [
      {
        title: "Task Name",
        dataIndex: "taskName",
        key: "taskName",
        width: 250,
        fixed: "left",
        render(text: any, record: any) {
          return (
            <>
              <span
                className="inlineTableCellLink"
                id={`task-${record?.taskId}`}
                onClick={() => {
                  navigate(`/pms/project/task-detail/${record.taskId}`, {
                    state: {
                      projectId,
                      projectName,
                      returnPath: location.pathname,
                      returnState: location.state,
                    },
                  });
                }}
              >
                {text}
              </span>
            </>
          );
        },
      },
      {
        title: "Owner",
        dataIndex: "taskOwnerName",
        key: "taskOwnerName",
        width: 250,
      },
      {
        title: "Start Date",
        dataIndex: "taskPlannedStartDate",
        key: "taskPlannedStartDate",
        width: 200,
        render: (text: string) => text || "N/A",
      },
      {
        title: "End Date",
        dataIndex: "taskPlannedEndDate",
        key: "taskPlannedEndDate",
        width: 200,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Priority",
        dataIndex: "priorityName",
        key: "priorityName",
        width: 150,
      },
      {
        title: "Progress",
        dataIndex: "completionPercentage",
        key: "completionPercentage",
        width: 200,
        render: (data: any) => {
          return (
            <Tooltip title={`${data}% Complete`} placement="top">
              <Progress
                strokeLinecap="butt"
                size={[186, 10]}
                percent={data}
                status="active"
                strokeColor={{
                  from: "#87d068",
                  to: "#87d068",
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "taskStatus",
        key: "taskStatus",
        width: 150,
        fixed: "right",
      },
    ],
    [dispatch]
  );

  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle="UserWise Planned Vs actual Report"
        pageDescription=""
        previousPath={{
          link: "/pms/project/reports",
          state: location.state,
        }}
        filters={{
          defaultFilters: {
            dateRange: {
              title: "Custom Date Range",
            },
          },
          customFilters: [
            {
              type: "multiple_selection",
              name: "UserId",
              label: "Users",
              options: OwnerFilterData,
            },
          ],
        }}
      />
      <Drawer
        title={`Tasks for ${selectedUser?.userName || "User"}`}
        width={1200}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Table
          columns={taskColumns}
          dataSource={tasksData}
          loading={loading}
          scroll={{ x: 1300 }}
          rowKey="taskId"
        />
      </Drawer>
    </>
  );
};

export default UserWisePlannedActualsReport;
